import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { License } from "@/pages/licenses/hooks/useMyLicenses";

export interface RedemptionBatchItem {
  licenseKey: string;
  productName: string | null;
  edition: string | null;
  seatCount: number;
  issuedAt: string | null;
}

export interface RedemptionBatch {
  batchId: string;
  count: number;
  productName: string | null;
  items: RedemptionBatchItem[];
}

export interface RedemptionExportLog {
  batch_id: string;
  kind: string;
  exported_count: number;
  exported_by_name: string | null;
  created_at: string;
}

interface UseRedemptionBatchesResult {
  batches: RedemptionBatch[];
  exportLogs: RedemptionExportLog[];
  total: number;
  loading: boolean;
  error: string | null;
  reload: () => void;
  logExport: (batchId: string, count: number, kind?: string) => Promise<void>;
}

// 把「用激活码核销」来的许可证按批次（license_codes.batch_id）归集，看每个批次各贡献了多少张。
// license_codes 仅 service role 可读，故通过 license-redeem 的 myRedemptionBatches 子动作获取。
export function useRedemptionBatches(licenses: License[], enabled: boolean): UseRedemptionBatchesResult {
  const [batches, setBatches] = useState<RedemptionBatch[]>([]);
  const [exportLogs, setExportLogs] = useState<RedemptionExportLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const redeemedCount = licenses.filter((item) => (item.source ?? "application") === "redeem").length;
    if (!enabled || redeemedCount === 0) {
      setBatches([]);
      setExportLogs([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke("license-redeem", {
        body: { action: "myRedemptionBatches" },
      });
      if (fnError) throw fnError;
      const result = data as {
        batches?: RedemptionBatch[];
        exportLogs?: RedemptionExportLog[];
        error?: string;
      } | null;
      if (result?.error) throw new Error(result.error);
      setBatches(result?.batches ?? []);
      setExportLogs(result?.exportLogs ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "批次统计加载失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  }, [licenses, enabled]);

  useEffect(() => {
    void load();
  }, [load]);

  // 记录一次「下载该批次 CSV」的导出留痕，并就地更新本地留痕列表，避免整块重新加载。
  const logExport = useCallback(async (batchId: string, count: number, kind = "redeem") => {
    try {
      const { data } = await supabase.functions.invoke("license-redeem", {
        body: { action: "logRedemptionExport", batchId, count, kind },
      });
      const result = data as { exportedByName?: string } | null;
      setExportLogs((prev) => [
        {
          batch_id: batchId,
          kind,
          exported_count: count,
          exported_by_name: result?.exportedByName ?? null,
          created_at: new Date().toISOString(),
        },
        ...prev,
      ]);
    } catch {
      // 留痕写入失败不影响已完成的下载
    }
  }, []);

  const total = batches.reduce((sum, item) => sum + item.count, 0);

  return { batches, exportLogs, total, loading, error, reload: load, logExport };
}